import { getProductsByCategory } from '@/data/catalogueData';
import { getCategoryMetadata } from '@/data/categoryMetadata';
import type { Product } from '@/data/catalogueData';
import type { CategoryColumn } from '@/data/categoryMetadata';
import { ProductImage } from './ProductImage';
import { ProfitMarginInput } from './ProfitMarginInput';
import { useCatalogueStore } from '@/store/catalogue-store';
import { calculateAdjustedPrice, formatPrice } from '@/lib/price-utils';
import './catalogue-print.css';
import logo from '../logo.png';

export function PDFCatalogue() {
  const profitMargin = useCatalogueStore((state) => state.profitMargin);

  const categoryOrder = [
    'KRAFT FOLDED TAKEOUT BOX',
    'CLAMSHELL TAKE-OUT BOX',
    'KRAFT FOOD TRAY',
    'MINERAL POWDER CLAMSHELLS',
    'MINERAL POWDER PLATES',
    'BLACK PLASTIC CONTAINERS',
    'BLACK SUSHI TRAYS',
    'DUAL COLOR BLACK CONTAINERS',
    'DUAL COLOR BLACK/CLEAR CLAMSHELLS',
    'CLEAR CLAMSHELL',
    'TRANSLUCID PP CLAMSHELL',
    'TAMPER-EVIDENT HINGED CONTAINER',
    'PLASTIC DELI CONTAINER',
    'PAPER KRAFT CONTAINER',
    'PAPER FOOD CONTAINER/PAPER SOUP CUP',
    'PORTION/SOUFFLE CUPS',
    'CLEAR DRINKING CUPS AND LIDS',
    'CUTLERY',
    'NAPKINS AND NAPKINS BANDS',
    'REGISTER ROLLS',
    'GLOVES',
    'STRAWS',
    'COFFEE AND COCKTAIL STIRRERS',
    'PLASTIC BAGS',
    'NON-HANDLE PAPER BAGS',
    'PAPER BAGS WITH HANDLE',
    'PAPER CLEANING SUPPLIES',
    'CAN LINERS/TRASH BAGS',
    'PAPER CUPS HOT DRINKS',
    'PAPER FRENCH FRY CUPS',
    'FOAM TAKEOUT CONTAINERS',
    'SCRUBBERS AND SPONGES',
    'WAREHOUSE SUPPLIES',
  ];

  return (
    <div className="catalogue-container" data-testid="catalogue-container">
      <div className="page-header">
        <img src={logo} alt="Supply City" className="company-logo" />
        <ProfitMarginInput />
      </div>

      {categoryOrder.map((category) => {
        const products = getProductsByCategory(category);
        if (products.length === 0) return null;

        const metadata = getCategoryMetadata(category);
        if (!metadata) return null;

        const renderCellValue = (column: CategoryColumn, product: Product) => {
          switch (column.type) {
            case 'code':
              return product.code;
            case 'product':
              return (
                <>
                  <div className="product-name">{product.name}</div>
                  {product.capacity && (
                    <div className="product-capacity">{product.capacity}</div>
                  )}
                </>
              );
            case 'productAndDescription':
              return (
                <>
                  <div className="product-name">{product.name}</div>
                  {product.capacity && (
                    <div className="product-capacity">{product.capacity}</div>
                  )}
                </>
              );
            case 'price':
            case 'pricePerCase':
              return formatPrice(calculateAdjustedPrice(product.price, profitMargin));
            case 'material':
              return product.material;
            case 'packing':
              return product.packing;
            case 'dimensions':
              return product.dimensions;
            case 'package':
              return product.package;
            case 'picture':
              return (
                <ProductImage
                  productCode={product.code}
                  alt={product.name}
                  className="product-image"
                />
              );
            default:
              return null;
          }
        };

        return (
          <div key={category} className="category-section" data-testid={`category-section-${category.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>
            <div className="category-header" data-testid={`category-header-${category.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>{category}</div>

            <table className="products-table" data-testid={`products-table-${category.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>
              <thead>
                <tr>
                  {metadata.columns.map((column) => (
                    <th key={column.type} className={column.cssClass}>
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid={`products-tbody-${category.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}>
                {products.map((product) => (
                  <tr key={product.id} data-testid={`product-row-${product.code}`}>
                    {metadata.columns.map((column) => (
                      <td key={column.type} className={column.cssClass} data-testid={`product-cell-${product.code}-${column.type}`}>
                        {renderCellValue(column, product)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}

      <div className="page-footer">
        Supply City LLC | 1460 NW 1st Court, Boca Raton, FL | ☎ (561) 410-4160 |
        ✉ sales@supplycityllc.com
      </div>
    </div>
  );
}
